import type { ECharts, EChartsInitOpts, SetOptionOpts, EChartsCoreOption } from 'echarts/core'
import type { echartsInstance } from './echarts-components'

const MERGE_PENDING_METHODS = ['setOption', 'resize']

let echartsLoadPromise: Promise<typeof echartsInstance> | null = null
let sharedEcharts: EchartsApi | null = null

function getCurrentEcharts(): typeof echartsInstance | null {
  return sharedEcharts?.echarts || null
}

function createResolvedVisibility() {
  return {
    promise: Promise.resolve(true),
    cancel() {},
  }
}

function resolveVisible(dom: HTMLElement | null | undefined) {
  if (!import.meta.client || !dom) return createResolvedVisibility()
  if (typeof IntersectionObserver === 'undefined') return createResolvedVisibility()

  const rect = dom.getBoundingClientRect()
  const isVisible =
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
    rect.left < (window.innerWidth || document.documentElement.clientWidth)

  if (isVisible) return createResolvedVisibility()

  let observer: IntersectionObserver | null = null
  let settled = false
  let resolveVisiblePromise!: (value: boolean) => void
  const promise = new Promise<boolean>((resolve) => {
    resolveVisiblePromise = resolve
    observer = new IntersectionObserver((entries) => {
      const [entry] = entries || []
      if (entry?.isIntersecting) {
        settled = true
        observer!.disconnect()
        resolve(true)
      }
    })

    observer.observe(dom)
  })

  return {
    promise,
    cancel() {
      if (settled) return
      settled = true
      observer?.disconnect()
      resolveVisiblePromise(false)
    },
  }
}

function loadEcharts(): Promise<typeof echartsInstance> {
  if (!import.meta.client) {
    return Promise.reject(new Error('ECharts can only be loaded on the client'))
  }
  if (echartsLoadPromise) return echartsLoadPromise

  echartsLoadPromise = import('./echarts-components')
    .then((m) => m.default || m.echartsInstance)
    .then((echarts) => {
      ;(window as any).echarts = echarts
      return echarts
    })
    .catch((error) => {
      echartsLoadPromise = null
      throw error
    })

  return echartsLoadPromise
}

type CancelFn = () => void

export interface ChartProxy {
  clear(...args: any[]): ReturnType<ECharts['clear']> | void
  dispose(...args: any[]): void
  resize(...args: any[]): ReturnType<ECharts['resize']> | void
  showLoading(...args: any[]): ReturnType<ECharts['showLoading']> | void
  hideLoading(...args: any[]): ReturnType<ECharts['hideLoading']> | void
  setOption(option: EChartsCoreOption, opts?: SetOptionOpts): ReturnType<ECharts['setOption']> | void
  dispatchAction(...args: any[]): ReturnType<ECharts['dispatchAction']> | void
  on(...args: any[]): ReturnType<ECharts['on']> | void
  off(...args: any[]): ReturnType<ECharts['off']> | void
  containPixel(...args: any[]): boolean
  convertFromPixel(...args: any[]): number[]
  convertToPixel(...args: any[]): number[]
  getZr(): any
  getInstance(): ECharts | null
  getDom(): HTMLElement | null
  ready(): Promise<ECharts | null>
}

function createChartProxy(
  chartPromise: Promise<ECharts | null>,
  cancelPendingInit?: CancelFn,
  initOptions?: { autoResize?: boolean },
  targetDom?: HTMLElement,
): ChartProxy {
  let chart: ECharts | null = null
  let disposed = false
  let onResize: (() => void) | null = null

  interface PendingCall {
    method: string
    args: any[]
  }

  const pendingCalls: PendingCall[] = []

  function queueCall(method: string, args: any[]) {
    if (disposed) return
    if (MERGE_PENDING_METHODS.includes(method)) {
      const last = pendingCalls.filter((c) => c.method === method).pop()
      if (last) {
        last.args = args
        return
      }
    }
    pendingCalls.push({ method, args })
  }

  function flushPendingCalls() {
    if (!chart) return
    const calls = pendingCalls.splice(0)
    for (const { method, args } of calls) {
      try {
        ;(chart as any)[method]?.apply(chart, args)
      } catch {
        // ignore flush errors
      }
    }
  }

  void (async () => {
    try {
      const instance = await chartPromise
      if (disposed) {
        instance?.dispose?.()
        return
      }
      chart = instance
      if (!chart) return

      if (initOptions?.autoResize !== false) {
        onResize = () => chart?.resize?.()
        window.addEventListener('resize', onResize)
      }

      flushPendingCalls()
    } catch {
      // ignore chart initialization errors
    }
  })()

  const invoke = (method: string, args: any[] = [], fallbackValue?: any) => {
    if (disposed) return fallbackValue

    if ((chart as any)?.[method]) {
      return (chart as any)[method](...args)
    }

    queueCall(method, args)
    return fallbackValue
  }

  const invokeZr = (method: string, args: any[] = [], fallbackValue?: any) => {
    if (disposed) return fallbackValue

    const zr = chart?.getZr?.()
    if ((zr as any)?.[method]) {
      return (zr as any)[method](...args)
    }

    pendingCalls.push({ method, args })
    return fallbackValue
  }

  return {
    clear(...args) {
      return invoke('clear', args)
    },
    dispose(...args) {
      disposed = true
      cancelPendingInit?.()
      pendingCalls.length = 0
      if (onResize) {
        window.removeEventListener('resize', onResize)
        onResize = null
      }
      return chart?.dispose ? Reflect.apply(chart.dispose, chart, args) : undefined
    },
    resize(...args) {
      return invoke('resize', args)
    },
    showLoading(...args) {
      return invoke('showLoading', args)
    },
    hideLoading(...args) {
      return invoke('hideLoading', args)
    },
    setOption(...args) {
      return invoke('setOption', args)
    },
    dispatchAction(...args) {
      return invoke('dispatchAction', args)
    },
    on(...args) {
      return invoke('on', args)
    },
    off(...args) {
      return invoke('off', args)
    },
    containPixel(...args) {
      return chart?.containPixel ? Reflect.apply(chart.containPixel, chart, args) : false
    },
    convertFromPixel(...args) {
      return chart?.convertFromPixel ? Reflect.apply(chart.convertFromPixel, chart, args) || [] : []
    },
    convertToPixel(...args) {
      return chart?.convertToPixel ? Reflect.apply(chart.convertToPixel, chart, args) || [] : []
    },
    getZr() {
      return chart?.getZr ? chart.getZr() : createZrProxy(() => chart, invokeZr)
    },
    getDom() {
      return chart?.getDom?.() || targetDom || null
    },
    getInstance() {
      return chart
    },
    ready() {
      return chartPromise
    },
  }
}

function createZrProxy(
  getChart: () => ECharts | null,
  invokeZr: (method: string, args: any[], fallbackValue?: any) => any,
) {
  return new Proxy(
    {},
    {
      get(_target, prop: string) {
        const zr = getChart()?.getZr?.()
        if (zr && typeof (zr as any)[prop] === 'function') {
          return (...args: any[]) => (zr as any)[prop](...args)
        }
        return (...args: any[]) => invokeZr(prop, args)
      },
    },
  )
}

export interface InitChartOptions extends EChartsInitOpts {
  lazy?: boolean
  autoResize?: boolean
}

interface EchartsApi {
  echarts: typeof echartsInstance | null
  load: () => Promise<typeof echartsInstance>
  init: (dom: HTMLElement | null | undefined, initOptions?: InitChartOptions) => ChartProxy | null
  getInstanceByDom: (dom: HTMLElement) => ECharts | null
}

function normalizeInitOptions(options: InitChartOptions = {}) {
  const { lazy, autoResize = true, ...echartsInitOptions } = options || {}

  return {
    lazy,
    autoResize,
    echartsInitOptions,
  }
}

export function useEcharts(): EchartsApi {
  const echartsApi: EchartsApi = {
    echarts: getCurrentEcharts(),
    load: () => Promise.reject(new Error('ECharts loader is not initialized')),
    init: () => null,
    getInstanceByDom: () => null,
  }

  echartsApi.load = () =>
    loadEcharts().then((echarts) => {
      echartsApi.echarts = echarts
      return echarts
    })

  echartsApi.init = (dom: HTMLElement | null | undefined, initOptions: InitChartOptions = {}): ChartProxy | null => {
    if (!import.meta.client || !dom) return null

    const { lazy, autoResize, echartsInitOptions } = normalizeInitOptions(initOptions)
    const visibility = lazy ? resolveVisible(dom) : createResolvedVisibility()

    const chartPromise: Promise<ECharts | null> = visibility.promise
      .then((visible) => {
        if (!visible) return null
        return echartsApi.load()
      })
      .then((echarts) => {
        if (!echarts) return null
        if (!dom?.isConnected) return null

        const existChart = echarts.getInstanceByDom?.(dom)
        if (existChart) existChart.dispose()

        return echarts.init(dom, null, echartsInitOptions)
      })

    return createChartProxy(chartPromise, visibility.cancel, { autoResize }, dom)
  }

  echartsApi.getInstanceByDom = (dom: HTMLElement) => {
    return echartsApi.echarts?.getInstanceByDom?.(dom) || null
  }

  return echartsApi
}

export function getSharedEchartsApi() {
  if (!sharedEcharts) {
    sharedEcharts = useEcharts()
  }

  return sharedEcharts
}
